<template>
  <div class="dashboard-page">
    <b-row class="mapContainer">
      <b-col cols="5" md="3" class="circuitList">
        <div class="searchBox sticky-top pb-3">
          <h2 class="mb-3">
            <div class="row justify-content-between">
              <div
                @click="switchCircuitPinMode()"
                class="col-4"
                id="switch-circuit-pin-mode"
              >
                Circuits
              <b-tooltip
                :target="'switch-circuit-pin-mode'"
                :container="'switch-circuit-pin-mode'"
                :title="'Turn on/off circuit view'"
              />
              </div>
              <div class="col-4">
                <b-button
                  id="add-circuit-button"
                  v-if="$base.isAllowed('createCircuitPole')"
                  size="sm"
                  variant="outline-primary"
                  class="float-right"
                  @click="circuitModalMode='Add'"
                  v-b-modal="'circuitModal'"
                >
                  <i class="fas fa-plus-circle"></i>
                </b-button>
                <b-tooltip
                  :target="'add-circuit-button'"
                  :container="'add-circuit-button'"
                  :title="'Add new circuit'"
                  triggers="hover"
                />
              </div>
            </div>
          </h2>
          <div class="mb-3">
            <FormInputWithClearButton v-bind:inputString.sync="search" class="mb-3" />
          </div>
        </div>
        <div v-if="isFetching" class="d-flex justify-content-center align-items-center" style="height: 300px;">
          <b-spinner style="width: 10rem; height: 10rem;"></b-spinner>
        </div>
        <div v-for="(c,c_index) in filteredCircuits" :key="c.uuid" class="circuitListContainer" v-if="canLoadCircuits">
          <b-row class="mb-3" no-gutters>
            <b-col class="text-center d-none d-sm-none d-md-none d-lg-block" cols="1">
              <a href="#" @click.prevent="expandCircuit(c.uuid)">
                <i
                  class="far"
                  :class="[(openAllCircuits[c.uuid]) ? 'fa-minus-square':'fa-plus-square']"
                ></i>
              </a>
            </b-col>
            <b-col cols="7" lg="8">
              <h5
                  class="circuitName"
                  :id="c.uuid"
              >
                <i
                  class="fas fa-exclamation-triangle text-warning"
                  v-if="!isVariableEmpty(circuitHasWarning(c))"
                  :class="circuitHasWarning(c)"
                ></i>
                {{c.name}}
              </h5>
            </b-col>
            <b-col cols="4" lg="3" class="text-right circuit-buttons">
              <div class="circuit-button-container">
                <div class="d-table-cell w-10 align-top">
                  <b-button
                    v-if="$base.isAllowed('createCircuitPole')"
                    size="xs"
                    variant="outline-primary"
                    @click="editCircuitUpdate(c.uuid)"
                    :id="'edit-circuit-button-' + c_index"
                  >
                    <i class="fas fa-edit"></i>
                  </b-button>
                  <b-tooltip
                    :target="'edit-circuit-button-' + c_index"
                    :title="'Edit Circuit'"
                  />
                </div>
                <div class="d-table-cell w-10 align-top">
                  <b-button
                    size="xs"
                    variant="outline-primary"
                    class="loadCircuitButton"
                    :id="'load-circuit-button-' + c_index"
                    @click.prevent="openCircuit(c.uuid)"
                  >
                    <i class="fas fa-search"></i>
                  </b-button>
                  <b-tooltip
                    :target="'load-circuit-button-' + c_index"
                    :title="'Load Circuit'"
                  />
                </div>
              </div>
            </b-col>
          </b-row>
          <b-collapse
            v-if="c && !isVariableEmpty(c.uuid)"
            :id="'circuit-'+c_index"
            v-model="openAllCircuits[c.uuid]"
          >
            <b-card
              v-if="openAllCircuits[c.uuid]"
              :id="'circuit-card-'+c_index"
              :border-variant="circuitHasWarning(c)"
              :header-bg-variant="circuitHasWarning(c)"
              no-body
            >
              <div slot="header">
                <b>Poles</b>
                <strong> - {{c.circuit_type}}</strong>
                <b-button
                  v-if="$base.isAllowed('createCircuitPole')"
                  :id="'add-pole-button-'+c_index"
                  size="xs"
                  variant="outline-primary"
                  class="float-right"
                  v-b-modal="'poleModal'"
                  @click.prevent="openAddPoleModal(c.uuid)"
                >
                  <i class="fas fa-plus-circle"></i>
                </b-button>
                <b-tooltip
                  :target="'add-pole-button-'+c_index"
                  :container="'circuit-card-'+c_index"
                  :title="'Add pole(s) in ' + c.name"
                />
              </div>
              <b-list-group flush>
                <b-list-group-item
                  :id="'circuit-'+c_index+'-pole-'+p_index"
                  :variant="poleHasWarning(pole)"
                  :button="!isVariableEmpty(pole.device)"
                  v-for="(pole, p_index) in c.poles"
                  :key="pole.uuid"
                  @click.prevent="openMarkerPopup(pole.device, c.uuid, pole)"
                >
                  <div class="d-table w-100">
                      <div class="d-table-cell">
                          <span class="text-ellipsis pr-1">{{ pole.pole_substation_name }}</span>
                          <span>[{{ pole.pole_substation_number }}]</span>
                        <div v-if="pole.device">
                          <i
                            class="fas fa-exclamation-triangle text-warning"
                            v-if="!isVariableEmpty(poleHasWarning(pole))"
                            :class="poleHasWarning(pole)"
                          ></i>
                          <small>{{pole.device.product_serial_num}}</small>
                        </div>
                      </div>
                      <div class="d-table-cell w-10 align-top">
                        <b-button
                          :id="'circuit-'+c_index+'-edit-pole-'+p_index"
                          v-if="$base.isAllowed('createCircuitPole')"
                          size="xs"
                          variant="outline-primary"
                          @click.prevent="openEditPoleModal(c.uuid, pole.uuid)"
                        >
                          <i class="fas fa-edit"></i>
                        </b-button>
                        <b-tooltip
                          :target="'circuit-'+c_index+'-edit-pole-'+p_index"
                          :container="'circuit-'+c_index+'-pole-'+p_index"
                          :title="'Edit pole'"
                        />
                      </div>
                    <div class="d-table-cell w-10 align-top">
                      <b-button
                        :id="'circuit-'+c_index+'-install-device-'+p_index"
                        v-if="!pole.device && $base.isAllowed('installDevice')"
                        size="xs"
                        variant="outline-primary"
                        v-b-modal="'sentinelInstallModal'"
                        @click.prevent="setSelectedPoleForSentinelInstall(pole)"
                      >
                        <i class="fas fa-plus-circle"></i>
                      </b-button>
                      <b-tooltip
                        v-if="!pole.device && $base.isAllowed('installDevice')"
                        :target="'circuit-'+c_index+'-install-device-'+p_index"
                        :container="'circuit-'+c_index+'-pole-'+p_index"
                        :title="'Install LineSIGHT at ' + pole.pole_substation_number"
                      />
                      <b-button
                        :id="'circuit-'+c_index+'-uninstall-device-'+p_index"
                        v-if="pole.device && $base.isAllowed('uninstallDevice')"
                        size="xs"
                        variant="outline-primary"
                        v-b-modal="'UninstallationModal'"
                        @click.prevent="setSelectedDeviceForSentinelUninstall(
                          {
                            product_serial_num: pole.device.product_serial_num,
                            pole: {
                              uuid: pole.uuid,
                              circuit_name: c.name,
                              pole_substation_number: pole.pole_substation_number}
                          })"
                      >
                        <i class="fas fa-minus-circle"></i>
                      </b-button>
                      <b-tooltip
                        v-if="pole.device && $base.isAllowed('uninstallDevice')"
                        :target="'circuit-'+c_index+'-uninstall-device-'+p_index"
                        :container="'circuit-'+c_index+'-pole-'+p_index"
                      >
                        Uninstall LineSIGHT
                        <br />
                        {{ pole.device.product_serial_num }}
                        <br />
                        at {{ pole.pole_substation_number }}
                      </b-tooltip>
                    </div>
                  </div>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-collapse>
          <hr />
        </div>
      </b-col>
      <b-col>
        <l-map :zoom="map.zoom" :bounds="circuit.bounds != null ? circuit.bounds : defaultMapBounds" ref="circuitMap">
          <l-marker
            :icon="setMarkerIconForCircuits(circuit)"
            v-for="(circuit) in circuits"
            :key="circuit.uuid"
            v-if="circuit.center != null && circuitsPinMode"
            :lat-lng="circuit.center"
            @add="storeMarker"
          >
          <l-popup>
              <div class="text-left">
                <p class="m-0">
                  <strong>Primary:</strong>
                  {{circuit.circuit_primary}}
                </p>
                <p class="m-0">
                  <strong>Circuit Name:</strong>
                  {{circuit.name}}
                </p>
                <p class="m-0">
                  <strong>Circuit Reference:</strong>
                  {{circuit.circuit_reference}}
                </p>
                <p class="m-0">
                  <strong>Voltage:</strong>
                  {{circuit.circuit_type}}
                </p>
              </div>
              <div class="text-center mt-3">
                <b-button
                  @click="openCircuit(circuit.uuid, true)"
                >View</b-button>
              </div>
            </l-popup>

          </l-marker>
          <l-tile-layer :url="map.url" :attribution="map.attribution"></l-tile-layer>
          <l-geo-json
            :geojson="currentGeoJson"
            :options="geoJsonOptions(circuit.circuit_type)"
            v-if="canDisplayGeoJson"
          />
          <l-marker
          :icon="setMarkerIconForDeviceCommunication(pole.device ? pole.device : null)"
          v-for="(pole) in circuit.poles"
          :key="pole.uuid"
          :options="{ alt: pole.device ? pole.device.product_serial_num : pole.pole_substation_name}"
          v-if="pole.device == null || pole.device.last_location"
          :lat-lng="[
            pole.device ? pole.device.last_location.latitude : pole.coordinates.latitude,
            pole.device ? pole.device.last_location.longitude : pole.coordinates.longitude
          ]"
            @add="storeMarker"
            >
            <l-popup
            :options="pole.device ? {}: { offset: [-6, -12] }"
            >
                <div class="text-left">
                  <p class="m-0">
                    <strong>Serial:</strong>
                    {{pole.device ? pole.device.product_serial_num : 'N/A'}}
                  </p>
                  <p class="m-0">
                    <strong>Last Communication:</strong>
                    {{ pole.device ? formatDate(pole.device.device_last_message_time) : 'N/A' }}
                  </p>
                  <p class="m-0">
                    <strong>Substation Name: </strong>
                    <span class="text-ellipsis-inline">
                      {{pole.pole_substation_name}}
                    </span>
                  </p>
                  <p class="m-0">
                    <strong>Substation Number: </strong>
                    <span class="text-ellipsis-inline">
                      {{pole.pole_substation_number}}
                    </span>
                  </p>
                  <p class="m-0">
                    <strong>HV Pole Number: </strong>
                    <span class="text-ellipsis-inline">
                      {{pole.hv_pole_number}}
                    </span>
                  </p>
                  <p class="m-0">
                    <strong>Type:</strong>
                    {{pole.type ? pole.type : 'Undefined'}}
                  </p>
                </div>
                <div class="d-flex flex-column gap-2 mt-3 justify-content-center align-items-center">
                   <b-button
                    v-if="pole.device"
                    :to="{ name: 'DevicePage', params: { serial: pole.device.serial }}"
                    variant="primary"
                    size="sm">
                    View
                  </b-button>

                  <a class="navigate-link" :href="`https://www.google.com/maps?q=${pole.coordinates.latitude},${pole.coordinates.longitude}`" 
                  @click.prevent="navigateToPole(pole.coordinates)">
                    Navigate to Pole
                  </a>
                 
                </div>
              </l-popup>
          </l-marker>
          <l-control position="bottomright" v-if="circuitsPinMode">
            <div class="map-legend">
              <div class="legend-title">Circuit Pin Colour Legend</div>
              <div class="legend-scale">
                <ul class="legend-labels">
                  <li>
                    <span class="black-background-pin"></span> Not Connected
                  </li>
                  <li>
                    <span class="red-background-pin"></span>Temperature Exceeded
                  </li>
                  <li>
                    <span class="orange-background-pin"></span>Power Loss
                  </li>
                  <li>
                    <span class="yellow-background-pin"></span>Low Battery
                  </li>
                  <li>
                    <span class="green-background-pin"></span>Connected
                  </li>
                </ul>
              </div>
            </div>
          </l-control>
          <l-control position="bottomright" v-else>
            <div class="map-legend">
              <div class="legend-title">Device Pin Colour Legend</div>
              <div class="legend-scale">
                <ul class="legend-labels">
                  <li>
                    <span class="black-background-pin"></span> Not Connected
                  </li>
                  <li>
                    <span class="red-background-pin"></span>Temperature Exceeded
                  </li>
                  <li>
                    <span class="orange-background-pin"></span>Power Loss
                  </li>
                  <li>
                    <span class="yellow-background-pin"></span>Low Battery
                  </li>
                  <li>
                    <span class="green-background-pin"></span>Connected
                  </li>
                </ul>
              </div>
            </div>
          </l-control>
        </l-map>
      </b-col>
    </b-row>

    <InstallationModal
      title="Install LineSIGHT"
      id="sentinelInstallModal"
      refName="deviceInstallModal"
      :poleToInstallDevice="poleToInstallDevice"
      @modalReset="handleModalReset"
      @updateCircuit="updateCircuit"
    />
    <UninstallationModal
      :device="deviceToUninstall"
      @modalReset="handleUninstallModalReset"
      @uninstallSuccess="handleUninstallSuccess"
      @updateCircuit="updateCircuit"
    />
    <CircuitModal
        :title="`${circuitModalMode} Circuit`"
        :circuit="circuit"
        id="circuitModal"
        refName="circuitModal"
        ref="circuitModal"
        :circuitModalMode="circuitModalMode"
        @updateCircuit="updateCircuit"
        @showPoleModal="showPoleModal"
        @switchToEditAndAddPole="switchToEditAndAddPole"
    />
    <PoleModal
      id="poleModal"
      :action="poleModalAction"
      :poleUuid="poleModalUuid"
      refName="poleModal"
      ref="poleModal"
      :circuit="circuit"
      @updateCircuit="updateCircuit"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { icon, divIcon } from "leaflet";
import { ExtraMarkers } from "leaflet-extra-markers";
import {
  LMap,
  LTileLayer,
  LMarker,
  LImageOverlay,
  LPopup,
  LControl,
  LRectangle,
  LFeatureGroup,
  LGeoJson
} from "vue2-leaflet";
import FormInputWithClearButton from "@/components/FormInputWithClearButton/FormInputWithClearButton";
import InstallationModal from "@/components/InstallationModal/InstallationModal";
import UninstallationModal from "@/components/UninstallationModal/UninstallationModal";
import CircuitModal from "@/components/CircuitModal/CircuitModal";
import PoleModal from "@/components/PoleModal/PoleModal";
import { basePerimeter } from "@/_perimeters/index";
import datetimeFormatter from '@/core/datetimeFormatterHelper';
import moment from "moment";
import { cloneDeep, isEmpty} from "lodash";
import {circuitService} from "@/_services";
import debounce from 'lodash.debounce';

export default {
  name: "Dashboard",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LImageOverlay,
    LPopup,
    LControl,
    LRectangle,
    LFeatureGroup,
    LGeoJson,
    FormInputWithClearButton,
    InstallationModal,
    UninstallationModal,
    CircuitModal,
    PoleModal,
  },
  data() {
    return {
      map: {
        zoom: 6,
        url: "https://{s}.tile.osm.org/{z}/{x}/{y}.png",
        attribution:
          '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
      },
      search: "",
      filteredCircuits:[],
      poleToInstallDevice: null,
      zIndexOffset: 254,
      imageURL: null,
      rectangleStyle: { color: "red", weight: 2, fillOpacity: 0.2 },
      selectedFault: -1,
      deviceToUninstall: null,
      poleModalAction: "add",
      poleModalUuid: "",
      popupFaultLocation: {
        latitude: null,
        longitude: null
      },
      defaultMapBounds: [
        [52.6188108303, -5.0992536835],
        [55, 0.378784]
      ],
      circuitModalMode: "Add",
      defaultGeoJson: {
        "type": "GeometryCollection",
        "geometries": []
      },
      currentGeoJson: null,
      canDisplayGeoJson: false,
      circuit: {
        "uuid": "",
        "name": "",
        "bounds": null,
        "circuit_type": "11kV",
        "circuit_geojson": {
          "type": "GeometryCollection",
          "geometries": []
        },
        "poles":[],
        "is_active": null,
        "circuit_primary": null,
        "circuit_reference": null,
      },
      circuits: [],
      canLoadCircuits: false,
      circuitsPinMode: true,
      isFetching: true,
    };
  },
  computed: {
    ...mapState("dashboard", {
      openedCircuits: state => state.openedCircuits,
      openAllCircuits: state => state.openAllCircuits,
      markersOnMap: state => state.markersOnMap,
      loadedCircuit: state => state.loadedCircuit,
      selectedDevice: state => state.selectedDevice
    }),
    ...mapState("layout", {
      hideInactiveCircuits: state => state.hideInactiveCircuits
    }),
    defaultCircuit() {
      return {
        "uuid": "",
        "name": "",
        "bounds": null,
        "circuit_type": "11kV",
        "circuit_geojson": {
          "type": "GeometryCollection",
          "geometries": []
        },
        "poles":[],
        "is_active": null,
        "circuit_primary": null,
        "circuit_reference": null,
      }
    },
 },
  watch: {
    async hideInactiveCircuits() {
      this.circuits = await circuitService.getAllCircuits()
    },
    search() { this.updateFilteredCircuits(); }
  },
  async created(){
    this.updateFilteredCircuits = debounce(() => {
      this.filteredCircuits = this.findBy();
    }, 800);
    this.updateFilteredCircuitsNow = () => {
      if (this.updateFilteredCircuits.cancel) this.updateFilteredCircuits.cancel();
      this.filteredCircuits = this.findBy();
    };

  },
  methods: {
    ...mapActions("dashboard", [
      "setLoadedCircuit",
      "setMarkersOnMap",
      "setSelectedDevice",
      "clearState"
    ]),
    ...mapActions("alert", ["success", "error"]),
    findBy: function() {
      let list = cloneDeep(this.circuits);
      let value = this.search;
      const _self = this;
      let searchValue = value.toLowerCase().trim();
      
      if (!searchValue) {
        Object.keys(_self.openAllCircuits).forEach(function(key) {
          _self.openAllCircuits[key] = _self.openedCircuits.includes(key);
        });
        return list;
      }
      let filter = circuit =>
        circuit.name.toLowerCase().includes(searchValue) ||
        circuit.circuit_primary.toLowerCase().includes(searchValue) ||
        circuit.circuit_reference.toLowerCase().includes(searchValue) ||
        circuit.poles.some(pole =>
          (!pole.hv_pole_number || !pole.pole_substation_number 
            ? false
            : pole.hv_pole_number.toLowerCase().includes(searchValue) ||
          pole.pole_substation_number.toLowerCase().includes(searchValue)) ||
          (pole.pole_substation_name ? pole.pole_substation_name.toLowerCase().includes(searchValue) : false) ||
          (!pole.device
          ? false
          : pole.device.product_serial_num
          .toLowerCase()
          .includes(searchValue))
        );
    
        let filteredList = list.filter(filter);
      
      filteredList.forEach(function(circuit) {
        if (!(circuit.name.toLowerCase().includes(searchValue) ||
              circuit.circuit_primary.toLowerCase().includes(searchValue) ||
              circuit.circuit_reference.toLowerCase().includes(searchValue))) {
          
          circuit.poles = circuit.poles.filter(pole =>
            (!pole.hv_pole_number || !pole.pole_substation_number 
            ? false
              : pole.hv_pole_number.toLowerCase().includes(searchValue) ||
                pole.pole_substation_number.toLowerCase().includes(searchValue)) ||
            (pole.pole_substation_name ? pole.pole_substation_name.toLowerCase().includes(searchValue) : false) ||
            (!pole.device 
              ? false
              : pole.device.product_serial_num
              .toLowerCase()
              .includes(searchValue))
          );
        }
        _self.openAllCircuits[circuit.uuid] = true;
      });

      return filteredList;
    },
    formatDate(value) {
      return datetimeFormatter(value);
    },
    async loadCircuit(uuid) {
      if (!uuid || uuid === this.loadedCircuit) return Promise.resolve();
      this.circuit = await circuitService.getOneCircuit(uuid)
      this.setLoadedCircuit(uuid);
      this.circuitsPinMode = false

      this.setMarkersOnMap([]);
     
    },
    deletePole(pole_uuid, circuit_uuid) {
      this.circuit.poles = this.circuit.poles.filter(pole => pole.uuid !== pole_uuid)
      let index_of_circuit = this.circuits.findIndex(c =>
        c.uuid === circuit_uuid)
      this.circuits[index_of_circuit] = this.circuit
    },
    deleteCircuit(circuit_uuid) {
      this.circuit = this.defaultCircuit
      this.circuits = this.circuits.filter(circuit => circuit.uuid !== circuit_uuid);
    },
    navigateToPole(coordinates) {
      if(!coordinates){
        this.error(`No GPS coordinates are available for pole`);
      }
      const googleMapsUrl = `https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}`;
      window.open(googleMapsUrl, "_blank");
    },
    async toggleOpenedCircuits(uuid) {
      if (!uuid) return;

      this.openAllCircuits[uuid] = !this.openAllCircuits[uuid];

      const index = this.openedCircuits.indexOf(uuid);
      if (index > -1) {
        this.openedCircuits.splice(index, 1);
      } else {
        this.openedCircuits.push(uuid);
      }

      if (this.markersOnMap.length === 0 || this.circuitsPinMode) {
        await this.loadCircuit(uuid);
      }
    },
    expandCircuit(uuid) {
        this.openAllCircuits[uuid] = !this.openAllCircuits[uuid];
        let index = this.openedCircuits.indexOf(uuid);

        if (index > -1) {
          this.openedCircuits.splice(index, 1);
        } else {
          this.openedCircuits.push(uuid);
        }
    },
    async editCircuitUpdate(uuid) {
      await this.updateCircuit(uuid)
      this.circuitModalMode = 'Edit'
      await this.loadCircuit(uuid)
      this.$refs.circuitModal.showModal();
    },
    async openCircuit(uuid, scrollToCircuit=false) {
      await this.loadCircuit(uuid)
      if (!isEmpty(this.circuit.circuit_geojson)) {
        this.currentGeoJson = this.circuit.circuit_geojson
        this.canDisplayGeoJson = true
      }
      else {
        this.canDisplayGeoJson = false
      }

      if (!this.openAllCircuits[uuid]) {
        await this.toggleOpenedCircuits(uuid);
      }
      if (scrollToCircuit) {
        this.scrollToLoadedCircuit()
      }
      // Check if all devices has GPS position
      for (const pole of this.circuit.poles) {
        if (pole.device && pole.device.last_location == null) {
          this.error('One or more devices on this circuit is missing GPS coordinates and is not shown');
        }
      }
    },
    storeMarker: function(e) {
      this.markersOnMap.push(e);
    },
    openAddPoleModal: function(circuitUuid) {
      this.loadCircuit(circuitUuid);
      this.poleModalAction = "add";
    },
    openEditPoleModal: async function(circuitUuid, poleUuid) {
      this.poleModalUuid = poleUuid;
      await this.loadCircuit(circuitUuid);
      this.poleModalAction = "edit";
      this.$bvModal.show("poleModal");
    },
    async openMarkerPopup(device, circuitUuid, pole) {
      if (!isEmpty(device)) {
        const deviceSerial = device.product_serial_num;
        if (circuitUuid !== this.loadedCircuit) {
          await this.loadCircuit(circuitUuid);
          this.$nextTick(() => this.openMarker(deviceSerial, deviceSerial, true));
        } else {
          this.openMarker(deviceSerial, deviceSerial, true);
        }
      } else {
        await this.loadCircuit(circuitUuid);
        this.$nextTick(() => this.openMarker(pole.hv_pole_number, pole.pole_substation_name));
      }
    },
    openMarker(identifier, name, isDevice = false) {
      const foundMarker = this.markersOnMap.find(e => 
        {
          if (e.target && e.target._popup && e.target._popup._content) {
            return e.target
              .getPopup()
              .getContent()
              .textContent.includes(identifier);
          } else {
            return false;
          }
        }
      );

      if (foundMarker) {
        const target = foundMarker.target;
        if (!target.getPopup().isOpen()) {
          target.openPopup();
          this.zIndexOffset += 1;
          target.setZIndexOffset(this.zIndexOffset);
          if (isDevice) this.setSelectedDevice(identifier);
        }
      } else {
        if (isDevice) {
          this.error(`No GPS coordinates are available for device ${name}`);
        } else {
          this.error(`No GPS coordinates are available for pole ${name}`);
        }
      }
    },
    setSelectedPoleForSentinelInstall: function(pole) {
      this.poleToInstallDevice = pole;
    },
    handleModalReset: function() {
      this.poleToInstallDevice = null;
    },
    setSelectedDeviceForSentinelUninstall: function(device) {
      this.deviceToUninstall = device;
    },
    handleUninstallModalReset: function() {
      this.deviceToUninstall = null;
    },
    async handleUninstallSuccess() {
      this.circuits = await circuitService.getAllCircuits()
    },
    setMarkerIconForDeviceCommunication: function(device) {
      // Zero Priority - Pole without device - different pin
      if (device == null) {
          return divIcon({
            html: "<b style='font-size:1.5vw'>I</b>",
            className: "pin",
            iconAnchor: [10, 33]
          });
      }
      // First Priority - Non Communicating Device - grey
      if (!device.online) {
        return icon({
          className: "offline-pin",
          iconUrl: require("@/assets/map-markers/marker-icon-2x-grey.png"),
          shadowUrl: require("@/assets/map-markers/marker-shadow.png"),
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });
      }
      
      // Second Priority - High Temperature - red
      if (device.temperature_exceeded) {
        return icon({
          className: "high-temp-pin",
          iconUrl: require("@/assets/map-markers/marker-icon-2x-red.png"),
          shadowUrl: require("@/assets/map-markers/marker-shadow.png"),
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });
      }

      // Third Priority - Loss of Battery connnection to mains - Orange
      if (device.mains_state && device.mains_state === "NO_VOLTAGE") {
        return icon({
          className: "loss-of-battery-connection-pin",
          iconUrl: require("@/assets/map-markers/marker-icon-2x-orange.png"),
          shadowUrl: require("@/assets/map-markers/marker-shadow.png"),
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });
      }

      // Fourth Priority - Low Battery - Yellow
      if (device.battery_state && device.battery_state === "LOW_LEVEL") {
        return icon({
          className: "low-battery-pin",
          iconUrl: require("@/assets/map-markers/marker-icon-2x-yellow.png"),
          shadowUrl: require("@/assets/map-markers/marker-shadow.png"),
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });
      }

      // Fifth Priority - OK - Green
      return icon({
        className: "ok-pin",
        iconUrl: require("@/assets/map-markers/marker-icon-2x-green.png"),
        shadowUrl: require("@/assets/map-markers/marker-shadow.png"),
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      });
    },
    circuitHasWarning: function(circuit) {

       // 4 Priority - Non Communicating Device - grey
      if (!isEmpty(circuit.poles)) {
        let polesWithOfflineDevice = circuit.poles.filter(
          pole => !isEmpty(pole.device) && !pole.device.online
        );
        if (polesWithOfflineDevice.length > 0) {
          return "offline-warning";
        }
      }
      // 1 Priority - High Temperature - Orange 
      if (!isEmpty(circuit.poles)) {
        let polesWithTemperatureExceeded = circuit.poles.filter(
          pole =>
            !isEmpty(pole.device) &&
            pole.device.temperature_exceeded &&
            pole.device.online
        );
        if (polesWithTemperatureExceeded.length > 0) {
          return "high-temp-warning";
        }
      }

      // 2 Priority - Low Battery - Yellow
      if (!isEmpty(circuit.poles)) {
        let polesWithLowBattery = circuit.poles.filter(
          pole =>
            !isEmpty(pole.device) &&
            pole.device.battery_state &&
            pole.device.battery_state === "LOW_LEVEL" &&
            pole.device.online
        );
        if (polesWithLowBattery.length > 0) {
          return "low-battery-warning";
        }
      }

      // 3 Priority - Loss of Battery connection to mains - Orange
      if (!isEmpty(circuit.poles)) {
        let polesWithLowBattery = circuit.poles.filter(
          pole =>
            !isEmpty(pole.device) &&
            pole.device.mains_state &&
            pole.device.mains_state === "NO_VOLTAGE" &&
            pole.device.online
        );
        if (polesWithLowBattery.length > 0) {
          return "loss-of-mains-warning";
        }
      }

     
      return "";
    },
    poleHasWarning: function(pole) {
      // First Priority - Non Communicating Device - grey
      if (!isEmpty(pole.device) && !pole.device.online) {
        return "offline-warning";
      }

      // Second Priority - High Temperature - Orange
      if (!isEmpty(pole.device) && pole.device.temperature_exceeded) {
        return "high-temp-warning";
      }

      // Third Priority - Loss of Battery connnection to mains- Orange
      if (
        !isEmpty(pole.device) &&
        pole.device.mains_state &&
        pole.device.mains_state === "NO_VOLTAGE"
      ) {
        return "loss-of-mains-warning";
      }

      // Fourth Priority - Low Battery - Yellow
      if (
        !isEmpty(pole.device) &&
        pole.device.battery_state &&
        pole.device.battery_state === "LOW_LEVEL"
      ) {
        return "low-battery-warning";
      }

      return "";
    },
    unloadCircuits: function() {
      this.circuit = {}
      this.clearState();
    },
    isVariableEmpty(variable) {
      return isEmpty(variable);
    },
    geoJsonOptions(circuit_type) {
      let options = {
        "style": {
          "color": "red"
        }
      }
      if (circuit_type === "6.6kV") {
        options.style = {}
      }
      return options
    },
    async updateCircuit(uuid=this.circuit.uuid, reLoadCircuit=false) {
      if (reLoadCircuit) {
        this.setLoadedCircuit("")
        await this.loadCircuit(uuid)
      }
      this.circuits = await circuitService.getAllCircuits()
      this.updateFilteredCircuitsNow()
    },
    setMarkerIconForCircuits(circuit) {
      let circuitWarning = this.circuitHasWarning(circuit)
      let circuitWarningColors = {
        "": "green",
        "high-temp-warning": "red",
        "loss-of-mains-warning": "orange",
        "offline-warning": "black",
        "low-battery-warning": "yellow",
      }
      return ExtraMarkers.icon({
        icon: "fa-flag",
        markerColor: circuitWarningColors[circuitWarning],
        shape: 'square',
        prefix: 'fa'
      });
    },
    switchCircuitPinMode() {
      if (!this.circuitsPinMode) {
        this.setMarkersOnMap([])
        this.map.bounds = this.defaultMapBounds
        this.circuitsPinMode = true
        this.unloadCircuits()
      }
    },
    scrollToLoadedCircuit() {
      let circuit = document.getElementById(this.loadedCircuit);
      circuit.scrollIntoView({behavior: 'smooth', block: 'center'})
    },
    showPoleModal(circuitUuid) {
      circuitService.getOneCircuit(circuitUuid).then(circuit => {
        this.circuit = circuit
        this.poleModalAction = "add"
        this.$root.$emit("bv::show::modal", 'poleModal');
      })
    },
    
    async switchToEditAndAddPole(newCircuitUuid) {
        this.circuitModalMode = 'Edit'
        await this.updateCircuit(newCircuitUuid, true)
        this.$nextTick(() => {
          this.$refs.circuitModal.addPoleRow()
        })
      }

  },
  async mounted() {
    this.setMarkersOnMap([]);
    this.circuits = await circuitService.getAllCircuits()
    this.updateFilteredCircuitsNow();
    this.canLoadCircuits = true
    if (this.circuit.bounds) {
      this.map.bounds = this.circuit.bounds;
    }
    if (this.selectedDevice) {
      this.$nextTick(() => {
        this.openMarkerForDevice(this.selectedDevice);
        let circuit_uuid = ""

        for (let circuit of this.circuits) {
          for (let pole of circuit.poles) {
            if (pole.device && pole.device.product_serial_num === this.selectedDevice) {
              circuit_uuid = circuit.uuid
              break
            }
          }
          if (circuit_uuid) {
            break
          }
        }
        this.openCircuit(circuit_uuid).then(() => {
          this.scrollToLoadedCircuit()
        })
      });
    }
    if (this.loadedCircuit) {
      await this.updateCircuit(this.loadedCircuit)
      await this.openCircuit(this.loadedCircuit)
      this.scrollToLoadedCircuit()
    }
    this.isFetching = false
  },
  beforeRouteLeave(to, from, next) {
    this.unloadCircuits();
    next();
  }
};
</script>

<style src="./Dashboard.scss" lang="scss" scoped />
